import type {
  FairUseReconciliationRow,
  FairUseReconciliationThresholds,
} from "@/api/admin/fair-use-reconciliation"

// =============================================================================
// Helpers for the "Metering coverage" card (round 3, spec R8).
//
// The one rule every function here obeys: **an absent number is unknown, not
// zero.** Azure Monitor omits a metric that has no data, the nightly job runs
// on the leader only, and a day nobody measured is simply not in the window.
// Unknown therefore reads as a PROBLEM (something we cannot see), never as a
// healthy day — which is also why `pricedZero` and `coverageUnknown` are
// issues rather than states the card renders in its calm tone.
// =============================================================================

/** How a day (or a single number on it) reads. `unknown` is a problem, not a blank. */
export type FairUseCoverageTone = "ok" | "warn" | "unknown"

/**
 * Everything that can be wrong with a measured day, worst first.
 *
 * Codes, not sentences: the card looks each one up in `adminDashboard.json`,
 * so the rule and its five translations cannot drift apart.
 */
export type FairUseCoverageIssue =
  | "driftTwoDays"
  | "drift"
  | "pricedZero"
  | "coverageUnknown"
  | "deltaUnknown"
  | "unmapped"

/** Coverage at or above this counts as complete enough to read as calm. */
export const FAIR_USE_COVERAGE_OK_PCT = 99

/** Rows shown in the day table; the rest of the window stays in the sparkline. */
export const FAIR_USE_COVERAGE_TABLE_DAYS = 7

/**
 * Every problem on one day.
 *
 * `deltaUnknown` is deliberately its own issue: a day whose ledger has rows but
 * whose Monitor side is missing produces no delta at all, and "no delta" must
 * not be mistaken for "no difference".
 */
export function fairUseCoverageIssues(
  row: FairUseReconciliationRow,
  thresholds: FairUseReconciliationThresholds
): FairUseCoverageIssue[] {
  const issues: FairUseCoverageIssue[] = []
  if (row.drifting_two_days) issues.push("driftTwoDays")
  else if (row.drifting || isOverThreshold(row, thresholds)) issues.push("drift")
  if (row.priced_pct === 0) issues.push("pricedZero")
  if (row.coverage_pct == null) issues.push("coverageUnknown")
  if (row.delta_tokens_pct == null && row.delta_sek_pct == null) issues.push("deltaUnknown")
  if (row.unmapped.length > 0) issues.push("unmapped")
  return issues
}

/**
 * The server already decides `drifting`; this re-checks it from the published
 * thresholds so an older backend that never learned the flag still colours.
 */
function isOverThreshold(
  row: FairUseReconciliationRow,
  thresholds: FairUseReconciliationThresholds
): boolean {
  const tokens = row.delta_tokens_pct
  const sek = row.delta_sek_pct
  if (tokens != null && Math.abs(tokens) > thresholds.token_pct) return true
  return sek != null && Math.abs(sek) > thresholds.sek_pct
}

/**
 * How a day reads overall.
 *
 * Only a day with nothing on the issue list AND coverage we can see is `ok`.
 * "Unknown" outranks "warn" on purpose: a number we cannot read is worse than
 * one we can read and dislike, because nobody can act on it yet.
 */
export function fairUseCoverageTone(
  row: FairUseReconciliationRow,
  thresholds: FairUseReconciliationThresholds
): FairUseCoverageTone {
  const issues = fairUseCoverageIssues(row, thresholds)
  if (issues.includes("coverageUnknown") || issues.includes("deltaUnknown")) return "unknown"
  if (issues.length > 0) return "warn"
  return (row.coverage_pct ?? 0) >= FAIR_USE_COVERAGE_OK_PCT ? "ok" : "warn"
}

/**
 * Tone of the coverage number itself.
 *
 * A day whose coverage IS known but whose delta is not keeps a warn tone
 * rather than the unknown one: the reader can see the coverage, so greying it
 * out would hide the number that is actually there.
 */
export function fairUseCoveragePctTone(
  row: FairUseReconciliationRow,
  thresholds: FairUseReconciliationThresholds
): FairUseCoverageTone {
  if (row.coverage_pct == null) return "unknown"
  const tone = fairUseCoverageTone(row, thresholds)
  return tone === "unknown" ? "warn" : tone
}

/** Tone of the priced share. 0 % is a problem; an absent share is unknown. */
export function fairUsePricedTone(pricedPct: number | null): FairUseCoverageTone {
  if (pricedPct == null) return "unknown"
  return pricedPct === 0 ? "warn" : "ok"
}

/** Tone of one difference against its threshold. Unknown is not agreement. */
export function fairUseDeltaTone(delta: number | null, threshold: number): FairUseCoverageTone {
  if (delta == null) return "unknown"
  return Math.abs(delta) > threshold ? "warn" : "ok"
}

/** Text class per tone — tokens only, so both themes follow the theme. */
export function fairUseCoverageToneClass(tone: FairUseCoverageTone): string {
  if (tone === "ok") return "text-success"
  if (tone === "warn") return "text-warning-foreground"
  return "text-muted-foreground"
}

/** A percentage with one decimal, or the caller's placeholder when unknown. */
export function formatCoveragePercent(
  value: number | null | undefined,
  locale: string,
  unknownLabel: string
): string {
  if (value == null || !Number.isFinite(value)) return unknownLabel
  try {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} %`
  } catch {
    return `${Math.round(value * 10) / 10} %`
  }
}

/**
 * A difference, always signed ("+0,4 %", "−1,2 %").
 *
 * The sign is the whole point: under-metering and over-metering are different
 * defects, and an unsigned "1,2 %" hides which one this is.
 */
export function formatCoverageDelta(
  value: number | null | undefined,
  locale: string,
  unknownLabel: string
): string {
  if (value == null || !Number.isFinite(value)) return unknownLabel
  try {
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
      signDisplay: "exceptZero",
    }).format(value)} %`
  } catch {
    const rounded = Math.round(value * 10) / 10
    return `${rounded > 0 ? "+" : ""}${rounded} %`
  }
}

/** `2026-09-15` → the locale's short day label; the raw value when it will not parse. */
export function formatCoverageDay(day: string, locale: string): string {
  const parsed = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return day
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(parsed)
  } catch {
    return day
  }
}

/** One point of the coverage sparkline, in the 0-100 space the card scales. */
export type FairUseCoveragePoint = { day: string; coverage: number }

/**
 * The window's known coverage values, OLDEST FIRST.
 *
 * A day without coverage is left out rather than drawn at 0: a line that dips
 * to the floor for a night the job never ran would read as a measured collapse.
 * The gap in the line is the honest picture.
 */
export function fairUseCoveragePoints(rows: FairUseReconciliationRow[]): FairUseCoveragePoint[] {
  return rows
    .filter((row) => row.coverage_pct != null && Number.isFinite(row.coverage_pct))
    .map((row) => ({ day: row.day, coverage: row.coverage_pct as number }))
    .sort((left, right) => left.day.localeCompare(right.day))
}

/**
 * The sparkline's polyline, in a 0..width / 0..height viewBox.
 *
 * Empty when fewer than two points are known — one dot is not a trend, and the
 * card shows the table instead.
 */
export function fairUseCoveragePolyline(
  points: FairUseCoveragePoint[],
  width: number,
  height: number
): string {
  if (points.length < 2) return ""
  const step = width / (points.length - 1)
  return points
    .map((point, index) => {
      const clamped = Math.max(0, Math.min(100, point.coverage))
      const y = height - (clamped / 100) * height
      return `${(index * step).toFixed(2)},${y.toFixed(2)}`
    })
    .join(" ")
}

/** The newest measured day, or undefined when nothing in the window was measured. */
export function fairUseLatestCoverageDay(
  rows: FairUseReconciliationRow[]
): FairUseReconciliationRow | undefined {
  return [...rows].sort((left, right) => right.day.localeCompare(left.day))[0]
}
